import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FileCategory, JobRecord } from '../../core/types';
import { openHistoryDb, type HistoryDatabase } from '../../history/history-db';
import { createHistoryRepository, type HistoryRepository } from '../../history/repository';

export interface HistoryFilters {
  category: FileCategory | 'all';
  status: JobRecord['status'] | 'all';
  keyword: string;
}

const PAGE_SIZE = 20;

export interface UseHistoryOptions {
  repository?: HistoryRepository;
  database?: Promise<HistoryDatabase>;
}

/** 转换记录：分页读取 + 筛选 + 清理；只读元数据与缩略图。 */
export function useHistory(options: UseHistoryOptions = {}) {
  const repository = useMemo(
    () => options.repository ?? createHistoryRepository({ database: options.database ?? openHistoryDb() }),
    [options.repository, options.database],
  );
  const [records, setRecords] = useState<JobRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<HistoryFilters>({ category: 'all', status: 'all', keyword: '' });
  const [selected, setSelected] = useState<JobRecord | null>(null);

  const query = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      category: filters.category,
      status: filters.status === 'all' ? undefined : filters.status,
      keyword: filters.keyword,
    }),
    [filters, page],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, count] = await Promise.all([
        repository.list(query),
        repository.count({ category: query.category, status: query.status, keyword: query.keyword }),
      ]);
      setRecords(rows);
      setTotal(count);
    } finally {
      setLoading(false);
    }
  }, [query, repository]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateFilters = useCallback((patch: Partial<HistoryFilters>) => {
    setPage(0);
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const clearAll = useCallback(async () => {
    await repository.clear();
    setSelected(null);
    setPage(0);
    await refresh();
  }, [refresh, repository]);

  const remove = useCallback(
    async (id: string) => {
      await repository.remove(id);
      setSelected((current) => (current && current.id === id ? null : current));
      await refresh();
    },
    [refresh, repository],
  );

  return {
    repository,
    records,
    total,
    page,
    pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    loading,
    filters,
    updateFilters,
    setPage,
    refresh,
    clearAll,
    remove,
    selected,
    setSelected,
  };
}
